import {
  subscriptionApi,
  type PurchasableProduct,
} from '@repo/subscription/api';
import { StateCreator } from 'zustand';

import { userStoreSelector, useUserStore } from '../userStore';

import { State, CreditModalType } from './types';

import { useCreditStore } from './index';

const initialState = {
  /**
   * whether the store was initialized
   */
  isInitialized: false,

  /**
   * stores the userId for which the store was initialized for
   * this is used to only initialize once for a user
   */
  initializedForUserId: null,

  /**
   * list of available products and prices for the user
   */
  products: [],

  activeModal: null,
};

export const stateCreator: StateCreator<State> = (set, get): State => ({
  ...initialState,

  /**
   * load relevant billing urls that are unique for a user
   */
  init: async (userId?: string | null | false): Promise<void> => {
    set({ isInitialized: false });
    if (!userId) {
      get().reset();

      set({ isInitialized: true });

      return;
    }

    if (get().initializedForUserId === userId) {
      set({ isInitialized: true });
      return;
    }

    await Promise.all([get().getProducts()]);

    set({ isInitialized: true, initializedForUserId: userId });
  },

  /**
   * reset user-specific state
   */
  reset: (): void => {
    const { products } = get();
    set({ ...initialState, products });
  },

  getProducts: async (): Promise<PurchasableProduct[]> => {
    const result = await subscriptionApi.getProducts();

    if (result.success) {
      set({ products: result.products });
      return result.products;
    }

    return [];
  },

  openModal: (modal: CreditModalType): void => {
    if (modal) {
      set({ activeModal: modal });
    }
  },

  closeModal: (): void => {
    set({ activeModal: null });
  },
});

useUserStore.subscribe(userStoreSelector.user, (user, prevUser) => {
  const { isInitialized, init, reset, getProducts } = useCreditStore.getState();

  if (!user?.id || user.id !== prevUser?.id) {
    reset();
  }

  if (isInitialized) {
    getProducts();
  } else if (user?.id) {
    init(user.id);
  }
});
